import { Box, debounce, Paper, Typography, useTheme } from '@mui/material'
import { DataGridPro, GridColDef, useGridApiRef } from '@mui/x-data-grid-pro'
import {
    GetCompanyEmployeesPaginatedQuery,
    GetCompanyQuery,
    OutreachStatus,
} from '__generated__/graphql'
import { format, parse } from 'date-fns'
import React from 'react'
import { Link } from 'react-router-dom'
import OutreachStatusPill from 'recruiter/components/OutreachStatusPill'
import { GetCompanyEmployeesPaginated } from 'recruiter/graphql/companies.gql'
import { recruiterRouteConfig } from 'recruiter/routes'
import { CenteredBox } from 'shared/components/CenteredBox'
import { LoadingPanel } from 'shared/components/LoadingPanel'
import { usePagination } from 'shared/hooks/usePagination'
import { getFullName, getPxAsRem } from 'shared/utils'

const getNoRowsOverlay = () => (
    <CenteredBox>
        <Typography>No employees found</Typography>
    </CenteredBox>
)

interface EmployeeListProps {
    data: GetCompanyQuery
    refetch: () => void
}

const fetchLimit = 10

const EmployeeList: React.FC<EmployeeListProps> = ({ data, refetch }) => {
    const gridApiRef = useGridApiRef()
    const theme = useTheme()

    const {
        data: employeesPaginated,
        isFetching,
        loadMoreData,
    } = usePagination<
        GetCompanyEmployeesPaginatedQuery['employeesPaginated']['data'][number]
    >({
        query: GetCompanyEmployeesPaginated,
        variables: {
            companyId: data?.company.id,
            limit: fetchLimit,
        },
        queryPath: 'employeesPaginated',
        limit: fetchLimit,
    })

    const columns: GridColDef[] = [
        {
            field: 'name',
            headerName: 'Name',
            flex: 1,
            renderCell: (params) => (
                <Link
                    to={`${recruiterRouteConfig.candidates.path}/${params.row.id}`}
                    style={{
                        textDecoration: 'none',
                        color: theme.palette.primary.main,
                    }}
                >
                    {getFullName(params.row.user ?? {}, true)}
                </Link>
            ),
        },
        {
            field: 'joined',
            headerName: 'Joined',
            flex: 1,
            renderCell: (params) =>
                params.row.currentWorkExperience?.startDate
                    ? format(
                          parse(
                              params.row.currentWorkExperience.startDate,
                              'MM-yyyy',
                              new Date()
                          ),
                          'MM/dd/yyyy'
                      )
                    : '-',
        },
        {
            field: 'title',
            headerName: 'Title',
            flex: 1,
            renderCell: (params) =>
                params.row.currentWorkExperience?.title?.value ?? '-',
        },
        {
            field: 'industry',
            headerName: 'Industry',
            flex: 1,
            renderCell: (params) =>
                params.row.currentWorkExperience?.industries
                    ?.map((industry) => industry.industry.value)
                    .join(', ') || '-',
        },
        {
            field: 'outreachStatus',
            headerName: 'Off Limits Status',
            flex: 1,
            renderCell: (params) => (
                <OutreachStatusPill
                    status={
                        params.row.outreachStatus?.status ||
                        OutreachStatus.NoStatus
                    }
                    nonPoachDate={params.row.outreachStatus?.nonPoachDate}
                />
            ),
        },
    ]

    const handleRowsScrollEnd = debounce(() => {
        loadMoreData()
    }, 300)

    return (
        <Paper
            elevation={1}
            sx={{
                padding: 2,
                borderRadius: 1,
            }}
        >
            <Box
                sx={{
                    justifyContent: 'space-between',
                    marginBottom: 2,
                }}
            >
                <Typography variant="h4">Employees</Typography>
            </Box>
            <DataGridPro
                sx={{
                    height: getPxAsRem(700),
                }}
                rows={employeesPaginated.map((row, index) => ({
                    ...row,
                    index,
                }))}
                getRowId={(row) => `${row.id}-${row.index}`}
                columns={columns}
                hideFooter
                disableColumnFilter
                disableColumnSelector
                disableColumnSorting
                disableColumnMenu
                loading={isFetching}
                onRowsScrollEnd={handleRowsScrollEnd}
                scrollEndThreshold={100}
                apiRef={gridApiRef}
                slots={{
                    noRowsOverlay: getNoRowsOverlay,
                    loadingOverlay: () => <LoadingPanel />,
                }}
            />
        </Paper>
    )
}

export default EmployeeList
